/**
 * EntitlementBanner — the demo's "wow moment" visual.
 *
 * Given a permission decision payload, it prominently shows:
 *   - returned_count / candidates_count ("Showing 2 of 5 matching documents")
 *   - the decision label (allow / partial / deny)
 *   - "N documents withheld due to your entitlements", broken down by the
 *     access-control reason (ACL not validated vs. group mismatch)
 *
 * Because the SAME query under a different persona yields a different
 * returned/withheld split, switching personas visibly changes this banner — the
 * whole point of the permission-aware retrieval story.
 *
 * It NEVER renders the title or content of a withheld document — only counts —
 * so no restricted information leaks to an unentitled persona.
 */

import type { ReactNode } from 'react';
import type { ExcludedCounts } from '../lib/retrieval';

export interface EntitlementBannerProps {
  decision: string;
  candidatesCount: number;
  returnedCount: number;
  excludedCounts: ExcludedCounts;
  /** Optional leak-free notice from the API; a fallback is computed if absent. */
  suppressionNotice?: string | null;
}

/** Tone tokens per decision label. */
function toneFor(decision: string): { dot: string; pill: string } {
  switch (decision.toLowerCase()) {
    case 'deny':
    case 'denied':
      return { dot: 'bg-red-500', pill: 'bg-red-100 text-red-800' };
    case 'partial':
      return { dot: 'bg-amber-500', pill: 'bg-amber-100 text-amber-800' };
    default:
      return { dot: 'bg-emerald-500', pill: 'bg-emerald-100 text-emerald-800' };
  }
}

function withheldText(total: number): string {
  const noun = total === 1 ? 'document' : 'documents';
  return `${total} ${noun} withheld due to your entitlements`;
}

export function EntitlementBanner({
  decision,
  candidatesCount,
  returnedCount,
  excludedCounts,
  suppressionNotice,
}: EntitlementBannerProps): ReactNode {
  const total = excludedCounts.total;
  const tone = toneFor(decision);
  const notice = suppressionNotice ?? (total > 0 ? withheldText(total) : null);

  return (
    <section
      data-testid="entitlement-banner"
      aria-label="Entitlement and suppression summary"
      className={`rounded-lg border p-4 ${
        total > 0 ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${tone.dot}`} />
          <span className="text-sm font-medium text-navy-900">
            Showing{' '}
            <span data-testid="returned-count" className="font-semibold">
              {returnedCount}
            </span>{' '}
            of{' '}
            <span data-testid="candidates-count" className="font-semibold">
              {candidatesCount}
            </span>{' '}
            matching {candidatesCount === 1 ? 'document' : 'documents'}
          </span>
        </div>
        <span
          data-testid="decision-pill"
          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide ${tone.pill}`}
        >
          {decision}
        </span>
      </div>

      {total > 0 ? (
        <div className="mt-3 border-t border-amber-200 pt-3">
          <p
            data-testid="withheld-notice"
            className="text-sm font-semibold text-amber-900"
          >
            {notice}
          </p>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-amber-800">
            {excludedCounts.for_group > 0 && (
              <span data-testid="withheld-for-group">
                {excludedCounts.for_group} restricted by group entitlement
              </span>
            )}
            {excludedCounts.for_acl > 0 && (
              <span data-testid="withheld-for-acl">
                {excludedCounts.for_acl} blocked by unvalidated access control
              </span>
            )}
          </div>
          <p className="mt-2 text-xs text-amber-700">
            Switch persona to see how your entitlements change these results — the
            withheld documents&apos; titles and content are never shown.
          </p>
        </div>
      ) : (
        <p className="mt-2 text-xs text-emerald-800">
          You are entitled to view every document matching this query.
        </p>
      )}
    </section>
  );
}

export default EntitlementBanner;
